import { useIntl } from '@redbubble/next/intl';
import { SEOJsonLd, Seo } from '@redbubble/next/seo';
import { AppProps } from '@redbubble/next/types';
import { usePageProps } from '@redbubble/next-provider';
import { useMemo } from 'react';
import type {
  AlternateProductPageUrl,
  DefaultInventoryItem,
  ReviewSummary,
} from 'types/shop-bff/types';
import { useCurrentInventoryStore } from '../../hooks/useCurrentInventoryItem';
import { ProductPageProps } from '../../types';
import { getMetaTags } from './metadata';

export interface SEOTagsProps {
  reviewSummary: ReviewSummary | null | undefined;
  availableProducts?: DefaultInventoryItem[] | null;
  alternateProductUrls?: AlternateProductPageUrl[] | null;
}

const SEOTags = ({
  reviewSummary,
  availableProducts,
  alternateProductUrls,
}: SEOTagsProps) => {
  const intl = useIntl();
  const { inventoryCategorySet, productDescriptions } = usePageProps<
    ProductPageProps & AppProps
  >();

  const { currentInventoryItem } = useCurrentInventoryStore((state) => state);
  const productDescription = productDescriptions[
    currentInventoryItem.productTypeId
  ] ?? [''];

  const {
    productTitle,
    titleTemplate,
    title,
    desc,
    canonicalUrl,
    productPageUrl,
    hasMatureContent,
    additionalMetaTags,
    openGraph,
    twitter,
    price,
    jsonLdImages,
    languageAlternates,
    noindex,
    longProductDescription,
    description,
    stockStatus,
  } = useMemo(
    () =>
      getMetaTags(
        inventoryCategorySet,
        currentInventoryItem,
        intl,
        productDescription,
        availableProducts,
        alternateProductUrls
      ),
    [
      inventoryCategorySet,
      currentInventoryItem,
      intl,
      productDescription,
      availableProducts,
      alternateProductUrls,
    ]
  );

  return (
    <>
      <Seo
        title={productTitle}
        titleTemplate={titleTemplate}
        description={desc}
        canonical={canonicalUrl}
        matureContent={hasMatureContent}
        additionalMetaTags={additionalMetaTags}
        openGraph={openGraph}
        twitterHandle={twitter}
        languageAlternates={languageAlternates}
        noindex={noindex}
      />
      <SEOJsonLd
        title={title}
        description={description}
        productDescription={longProductDescription}
        price={price}
        productPageUrl={productPageUrl ?? ''}
        jsonLdImages={jsonLdImages}
        reviewSummary={reviewSummary}
        stockStatus={stockStatus}
      />
    </>
  );
};

export { SEOTags };
